import { orderingPreset, hangerGroup } from './orderingPresets';
import { orderingGroups } from './orderingModel';

// Housing sheets saved before hanger bars existed get the section added after
// HOUSING STYLE, so the option shows up without re-applying the template.
function withHangerBars(groups, kind) {
  if (kind !== 'housing' || !groups.length) return groups;
  const existing = groups.findIndex(g => g.id === 'hanger' || (g.columns || []).some(c => c.id === 'HB'));
  if (existing >= 0) return groups.map((g, i) => i !== existing || !/hanger bars/i.test(g.label || '') ? g
    : { ...g, label: 'HARDWARE', columns: (g.columns || []).map(c => c.id === 'HB' ? { ...c, title: 'HARDWARE' } : c) });
  const next = [...groups];
  const at = next.findIndex(g => g.id === 'style');
  next.splice(at < 0 ? next.length : at + 1, 0, hangerGroup());
  return next;
}

// One-time, non-destructive upgrade. Previously trimmed options return unchecked.
// Existing selections, custom labels/options, and saved visibility are retained.
export function restoreOrderingOptions(o, kind) {
  if (o.optionLibraryVersion === 2) return { ...o, groups: withHangerBars(o.groups || [], kind) };
  const old = orderingGroups(o, kind);
  if (!old.length) return { ...o, groups: [], optionLibraryVersion: 2 };
  const typeKey = o.typeKey || (kind === 'housing' ? 'housing' : old.some(g => /^T(?:-|$)/.test(g.prefix || '') || (g.columns || []).some(c => /adapter/i.test(c.title))) ? 'track' : 'downlight');
  const library = orderingPreset(typeKey);
  const libraryColumns = library.groups.flatMap(g => g.columns || []);
  const choose = (g, choices) => ({ ...g, columns: g.columns.map(c => ({ ...c, selected: choices[c.id] || c.selected,
    options: c.options.map(option => ({ ...option, enabled: option.code === (choices[c.id] || c.selected) })),
  })) });
  let groups = old.flatMap(g => {
    if (g.prefix == null) return [g];
    if (g.sourceGroups?.length) return g.sourceGroups;
    const code = String(g.prefix).trim();
    const trim = /^([RPSWC])-([A-Z]{3})([TSMLG])([RSP])$/.exec(code);
    const track = /^T-([A-Z]{3})([TSMLG])$/.exec(code);
    const housing = /^H-([RSP])(\d{2})$/.exec(code);
    if (typeKey === 'downlight' && trim) return [choose(library.groups[0], { M: trim[1] }), choose(library.groups[1], { F: trim[2], Z: trim[3], P: trim[4] })];
    if (typeKey === 'track' && track) return [library.groups[0], choose(library.groups[1], { F: track[1], Z: track[2] })];
    if (typeKey === 'housing' && housing) return [library.groups[0], choose(library.groups[1], { P: housing[1], Z: housing[2] })];
    if (typeKey === 'housing' && g.id === 'aperture' && /^[RSP]\d{2}$/.test(code)) return [choose(library.groups[1], { P: code[0], Z: code.slice(1) })];
    return [g];
  });
  groups = groups.map(g => ({ ...g, columns: (g.columns || []).map(c => {
    const base = libraryColumns.find(b => b.id === c.id);
    if (!base) return c;
    const existing = c.options || [];
    return { ...c, options: [...existing, ...base.options.filter(option => !existing.some(x => x.code === option.code)).map(option => ({ ...option, enabled: false }))] };
  }) }));
  groups = withHangerBars(groups, kind);
  const present = new Set(groups.flatMap(g => (g.columns || []).map(c => c.id)));
  library.groups.forEach(g => {
    const missing = (g.columns || []).filter(c => !present.has(c.id));
    if (!missing.length) return;
    const sameGroup = groups.find(x => (x.columns || []).some(c => g.columns.some(gc => gc.id === c.id)));
    if (sameGroup) sameGroup.columns = [...sameGroup.columns, ...missing.map(c => ({ ...c, enabled: false }))];
    else groups.push({ ...g, id: 'restored-' + g.id, columns: missing, enabled: false });
    missing.forEach(c => present.add(c.id));
  });
  return { ...o, groups, typeKey, optionLibraryVersion: 2 };
}